import { useMemo, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Sparkles } from "lucide-react";
import { IMAGES } from "@/data";

export const scrollToId = (id) => {
  const el = document.querySelector(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72 });
  else el.scrollIntoView({ behavior: "smooth" });
};

const LINES = ["COTTON CANDY", "FROM ANOTHER", "GALAXY"];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yOrb = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const stars = useMemo(
    () => Array.from({ length: 55 }, (_, i) => ({
      id: i,
      top: Math.random() * 100,
      left: Math.random() * 100,
      size: Math.random() * 2 + 1,
      delay: Math.random() * 4,
    })),
    []
  );

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen overflow-hidden bg-nebula flex items-center pt-24 pb-16">
      {stars.map((s) => (
        <span key={s.id} className="twinkle-star absolute rounded-full bg-white" style={{
          top: `${s.top}%`, left: `${s.left}%`, width: s.size, height: s.size, animationDelay: `${s.delay}s`,
        }} />
      ))}

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1.15fr_0.85fr] gap-14 items-center w-full">
        <motion.div style={{ y: yText, opacity: fade }}>
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="inline-flex items-center gap-2 rounded-full border border-cosmic-cyan/30 bg-cosmic-cyan/10 px-4 py-1.5 mb-8"
            data-testid="hero-badge">
            <Sparkles size={14} className="text-cosmic-cyan" />
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-cosmic-cyan">Handcrafted · Small Batch · Live Spun</span>
          </motion.div>

          <h1 className="font-display font-black tracking-tight leading-[0.95] text-5xl sm:text-7xl lg:text-8xl text-white">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 2 ? "text-gradient-candy" : ""}`}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.2 + i * 0.14, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.7 }}
            className="mt-7 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed"
            data-testid="hero-subtitle">
            16 deep-space flavors spun into fluffy clouds. Order containers for pickup, or book live
            spinning at your event with unlimited cones for every guest.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-4">
            <button data-testid="hero-order-cta" onClick={() => scrollToId("#flavors")}
              className="rounded-full bg-gradient-to-r from-cosmic-pink to-cosmic-purple px-8 py-4 font-display font-bold text-white transition-transform duration-300 hover:scale-105 glow-pink">
              Order Containers
            </button>
            <button data-testid="hero-book-cta" onClick={() => scrollToId("#packages")}
              className="rounded-full glass px-8 py-4 font-display font-bold text-white transition-all duration-300 hover:border-cosmic-cyan/50 hover:text-cosmic-cyan">
              Book Live Spinning
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.15, duration: 0.8 }}
            className="mt-12 flex flex-wrap gap-x-10 gap-y-4"
            data-testid="hero-stats">
            {[["16", "Signature Flavors"], ["∞", "Cones at Events"], ["6", "Ways to Pay"]].map(([num, label]) => (
              <div key={label}>
                <div className="font-display text-3xl font-black text-white">{num}</div>
                <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400 mt-1">{label}</div>
              </div>
            ))}
          </motion.div>
        </motion.div>

        <motion.div style={{ y: yOrb }} className="relative hidden lg:flex justify-center" data-testid="hero-orb">
          <motion.div
            animate={{ y: [0, -18, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="relative w-[380px] h-[380px]">
            <div className="absolute -inset-10 rounded-full bg-[radial-gradient(circle,rgba(255,42,133,0.35)_0%,rgba(157,78,221,0.18)_45%,transparent_70%)] blur-2xl" />
            <img src={IMAGES.spun_cloud_pink} alt="Spun pink cotton candy cloud"
              className="relative w-full h-full object-cover rounded-full border border-white/20 shadow-[0_0_90px_rgba(255,42,133,0.45)]" />
            <svg viewBox="0 0 400 400" className="animate-spin-slow absolute -inset-8 w-[calc(100%+64px)] h-[calc(100%+64px)]">
              <defs>
                <linearGradient id="ringg" x1="0" y1="0" x2="400" y2="400" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#FF2A85" /><stop offset="1" stopColor="#00F0FF" />
                </linearGradient>
              </defs>
              <ellipse cx="200" cy="200" rx="188" ry="62" fill="none" stroke="url(#ringg)" strokeWidth="2.5" opacity="0.7" transform="rotate(-18 200 200)" />
            </svg>
            <motion.div
              initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 1.2, duration: 0.6 }}
              className="glass absolute -bottom-2 -left-6 rounded-2xl px-5 py-3 glow-cyan">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-cosmic-cyan">Best Seller</div>
              <div className="font-display font-bold text-white">Cosmic Blue Razz</div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("#flavors")}
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }}
        className="absolute bottom-7 left-1/2 -translate-x-1/2 text-slate-400 hover:text-cosmic-cyan transition-colors"
        aria-label="Scroll to flavors">
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity }}>
          <ArrowDown size={22} />
        </motion.div>
      </motion.button>
    </section>
  );
};

export default Hero;
